/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentRef, Injectable, Renderer2 } from '@angular/core';

import type { ProcessRowParams } from 'ag-grid-community';
import { gridRowCacheKey } from '../../grid-internal.utils';
import { GRID_EVENT } from '../../grid-public-events';
import { GridFeatureReadyParams } from '../feature.interfaces';
import { AbstractGridOptionBarFeature } from './abstract-grid-option-bar-feature';
import { GridRowInitParams, GridRowOptionsComponent } from './grid-row-options-component';

interface OptionBarConfig {
  componentRef: ComponentRef<GridRowOptionsComponent>;
  row: GridRowInitParams;
}

@Injectable()
export class GridOptionBarFeature extends AbstractGridOptionBarFeature {
  private optionBars: { [id: string]: OptionBarConfig } = {};

  private nextFrame: number = 0;

  constructor(protected renderer: Renderer2) {
    super();

    this.scheduleUpdatePosition = this.scheduleUpdatePosition.bind(this);
  }

  onGridReady(params: GridFeatureReadyParams): void {
    super.onGridReady(params);

    this.api.addEventListener(GRID_EVENT.columnResized, this.scheduleUpdatePosition);
    this.api.addEventListener(GRID_EVENT.modelUpdated, this.scheduleUpdatePosition);
    this.api.addEventListener(GRID_EVENT.gridSizeChanged, this.scheduleUpdatePosition);
    this.getScrollableContainer().addEventListener('scroll', this.scheduleUpdatePosition);
  }

  onDestroy(): void {
    if (this.api) {
      this.api.removeEventListener(GRID_EVENT.columnResized, this.scheduleUpdatePosition);
      this.api.removeEventListener(GRID_EVENT.modelUpdated, this.scheduleUpdatePosition);
      this.api.removeEventListener(GRID_EVENT.gridSizeChanged, this.scheduleUpdatePosition);
      this.getScrollableContainer().removeEventListener('scroll', this.scheduleUpdatePosition);

      cancelAnimationFrame(this.nextFrame);
    }
  }

  process(params: ProcessRowParams): void {
    if (!params.eRow) {
      return;
    }

    this.createAndAttachComponent(params);

    params.addRenderedRowListener(GRID_EVENT.virtualRowRemoved, () => {
      this.detachAndDestroyComponent(params);
    });
  }

  private scheduleUpdatePosition(): void {
    if (this.nextFrame) {
      return;
    }

    this.nextFrame = requestAnimationFrame(() => {
      this.nextFrame = 0;
      this.updatePosition();
    });
  }

  private updatePosition(): void {
    Object.keys(this.optionBars).forEach((id) => {
      const optionBar = this.optionBars[id];

      this.renderer.setStyle(
        optionBar.componentRef.location.nativeElement,
        'right',
        this.getBestRightPosition(optionBar.row) + 'px',
      );
    });
  }

  private createAndAttachComponent(row: GridRowInitParams): void {
    const component = this.optionBars[gridRowCacheKey(row.node)];
    if (component) {
      this.detachAndDestroyComponent(row);
    }

    const componentRef = this.createComponent();

    this.attachComponent(row, componentRef);

    this.optionBars[gridRowCacheKey(row.node)] = { row, componentRef };
  }

  private detachAndDestroyComponent(row: GridRowInitParams): void {
    this.detachComponent(row);

    const optionBar = this.optionBars[gridRowCacheKey(row.node)];

    if (optionBar) {
      optionBar.componentRef.destroy();
      delete this.optionBars[gridRowCacheKey(row.node)];
    }
  }

}
